import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, updateDoc, doc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://us-central1-cura-a5c8a.cloudfunctions.net/api';

export default function SupportTab() {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'supportEmails'), orderBy('receivedAt', 'desc')));
      setEmails(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function markRead(id) {
    await updateDoc(doc(db, 'supportEmails', id), { read: true });
    setEmails(prev => prev.map(e => e.id === id ? { ...e, read: true } : e));
  }

  async function sendReply(email) {
    if (!replyText.trim()) return;
    setReplying(true);
    try {
      const token = await getAuth().currentUser?.getIdToken();
      const res = await fetch(`${API_BASE}/api/admin/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ to: email.fromEmail, subject: `Re: ${email.subject || 'Your message to Cura'}`, message: replyText }),
      });
      if (!res.ok) throw new Error('Failed to send');
      await updateDoc(doc(db, 'supportEmails', email.id), { replied: true });
      setEmails(prev => prev.map(e => e.id === email.id ? { ...e, replied: true } : e));
      setReplyText('');
      setExpanded(null);
      alert('Reply sent!');
    } catch (e) {
      alert('Failed to send reply. Check the backend admin reply endpoint.');
    } finally {
      setReplying(false);
    }
  }

  const filtered = emails.filter(e =>
    !search || [e.fromEmail, e.subject, e.body].join(' ').toLowerCase().includes(search.toLowerCase())
  );
  const unread = emails.filter(e => !e.read).length;

  return (
    <div>
      <div style={s.pageHeader}>
        <h2 style={s.pageTitle}>Support Emails</h2>
        <p style={s.pageSubtitle}>{emails.length} total · {unread} unread</p>
      </div>

      <div style={s.setupNotice}>
        <span style={s.setupIcon}>ℹ️</span>
        <span>
          Support emails from <strong>contact@thecura.app</strong> need to be forwarded to Firebase to appear here.
          Set up Gmail forwarding to the <code>ingestSupportEmail</code> Cloud Function endpoint.
        </span>
      </div>

      <div style={s.filters}>
        <input style={s.searchInput} placeholder="Search emails..." value={search} onChange={e => setSearch(e.target.value)} />
        <button style={s.refreshBtn} onClick={load}>↺ Refresh</button>
      </div>

      {loading ? (
        <div style={s.loading}>Loading emails...</div>
      ) : filtered.length === 0 ? (
        <div style={s.empty}>No support emails yet.</div>
      ) : (
        <div style={s.list}>
          {filtered.map(email => {
            const isOpen = expanded === email.id;
            const ts = email.receivedAt?.toDate?.()?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) || '';
            return (
              <div
                key={email.id}
                style={{ ...s.card, ...(email.read ? {} : s.cardUnread) }}
                onClick={() => { if (!email.read) markRead(email.id); setExpanded(isOpen ? null : email.id); }}
              >
                <div style={s.cardTop}>
                  <div style={s.cardMeta}>
                    {!email.read && <span style={s.unreadDot} />}
                    <span style={s.emailFrom}>{email.fromEmail || 'Unknown'}</span>
                    {email.replied && <span style={s.repliedBadge}>Replied</span>}
                  </div>
                  <span style={s.date}>{ts}</span>
                </div>
                <p style={s.subject}>{email.subject || '(No subject)'}</p>
                {isOpen && (
                  <div onClick={e => e.stopPropagation()}>
                    <p style={s.body}>{email.body}</p>
                    <div style={s.replyBox}>
                      <textarea
                        style={s.replyTextarea}
                        placeholder={`Reply to ${email.fromEmail}...`}
                        value={replyText}
                        onChange={e => setReplyText(e.target.value)}
                        rows={5}
                      />
                      <button style={s.sendBtn} disabled={replying || !replyText.trim()} onClick={() => sendReply(email)}>
                        {replying ? 'Sending...' : 'Send from contact@thecura.app'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const s = {
  pageHeader: { marginBottom: 20 },
  pageTitle: { fontSize: 24, fontWeight: 800, color: 'var(--green-900)' },
  pageSubtitle: { fontSize: 14, color: 'var(--text-muted)', marginTop: 4 },
  setupNotice: {
    display: 'flex', alignItems: 'flex-start', gap: 10, padding: '14px 16px',
    background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 'var(--radius-sm)',
    fontSize: 13, color: '#5d4037', lineHeight: 1.6, marginBottom: 20,
  },
  setupIcon: { fontSize: 16, flexShrink: 0, marginTop: 1 },
  filters: { display: 'flex', gap: 10, marginBottom: 20, alignItems: 'center' },
  searchInput: {
    flex: 1, padding: '10px 14px', borderRadius: 'var(--radius-sm)',
    border: '1.5px solid var(--border)', background: 'white', fontSize: 14,
  },
  refreshBtn: { padding: '10px 16px', background: 'var(--green-100)', color: 'var(--green-800)', borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 700 },
  loading: { textAlign: 'center', padding: 48, color: 'var(--text-muted)' },
  empty: { textAlign: 'center', padding: 48, color: 'var(--text-muted)' },
  list: { display: 'flex', flexDirection: 'column', gap: 10 },
  card: {
    background: 'white', borderRadius: 'var(--radius-md)', padding: '18px 20px',
    boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border)', cursor: 'pointer',
  },
  cardUnread: { borderLeft: '3px solid var(--green-600)' },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },
  cardMeta: { display: 'flex', alignItems: 'center', gap: 8 },
  unreadDot: { width: 8, height: 8, borderRadius: '50%', background: 'var(--green-600)', flexShrink: 0 },
  emailFrom: { fontSize: 14, fontWeight: 700, color: 'var(--green-900)' },
  repliedBadge: { fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 999, background: '#e3f2fd', color: '#1565c0' },
  date: { fontSize: 12, color: 'var(--text-muted)' },
  subject: { fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' },
  body: { fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.7, marginTop: 14, whiteSpace: 'pre-wrap' },
  replyBox: { marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 16 },
  replyTextarea: {
    width: '100%', padding: '12px 14px', background: 'var(--green-50)',
    border: '1.5px solid var(--border)', borderRadius: 'var(--radius-sm)',
    fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.6, resize: 'vertical', marginBottom: 10,
  },
  sendBtn: {
    padding: '10px 20px', background: 'linear-gradient(135deg, var(--green-800), var(--green-600))',
    color: 'white', borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 700,
  },
};
